import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ChevronLeftIcon } from '@heroicons/react/24/outline';

interface MobileLayoutProps {
  children: React.ReactNode;
  title?: string; // Keep for potential future use but don't display it
}

const MobileLayout: React.FC<MobileLayoutProps> = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const isHomePage = location.pathname === '/';

  const handleTitleClick = () => {
    navigate('/');
  };

  const handleBackClick = () => {
    // Check if the current path is one of the routes that might use MediaLibrary with URL parameters
    const path = location.pathname;
    const hasSearchParams = location.search.length > 0;

    // List of problematic routes that use MediaLibrary and add URL parameters
    const mediaLibraryRoutes = [
      '/sacred-texts',
      '/saved',
      '/saved-recordings',
      '/qa-library',
      '/meditation-library',
      '/media',
      '/introduction'
    ];

    if (mediaLibraryRoutes.includes(path) && hasSearchParams) {
      // For media library routes with search params, navigate to home
      navigate('/');
    } else {
      // For all other routes, use normal back navigation
      navigate(-1);
    }
  };

  return (
    <div className="min-h-screen bg-white dark:bg-grey-900" style={{ height: '100%', overflow: 'auto', WebkitOverflowScrolling: 'touch' }}>
      {/* Centered container for large screens */}
      <div className="max-w-[600px] mx-auto shadow-lg min-h-screen relative bg-white dark:bg-grey-900">
        {/* Fixed overlay for notch area that extends below */}
        {/* Filler for safe area above header. Ensures correct color in both light and dark mode. */}
        <div
          className="fixed top-0 left-0 right-0 z-[60] bg-white dark:bg-grey-900"
          style={{
            height: 'calc(env(safe-area-inset-top, 20px) + 2px)',
            maxWidth: '600px',
            margin: '0 auto',
            pointerEvents: 'none', // Don't block touch events for scrolling
            // Fallback for dark mode in case Tailwind class is overridden or not applied
            backgroundColor: typeof window !== 'undefined' && document.documentElement.classList.contains('dark') ? '#111827' : undefined
          }}
        />

        {/* Content container with top padding */}
        <div className="relative" style={{ paddingTop: 'env(safe-area-inset-top, 20px)' }}>

          {/* Header with extended background */}
          <header className="sticky top-0 z-50 bg-white dark:bg-grey-900 border-b border-gray-200 dark:border-grey-800">
            {/* Extended background that starts from the very top of the viewport */}
            <div className="absolute top-[-100vh] left-0 right-0 h-[100vh] bg-white dark:bg-grey-900" />
            {/* Additional background layer for smooth scrolling */}
            <div className="absolute top-[-50px] left-0 right-0 h-[50px] bg-white dark:bg-grey-900" />
            <div className="flex items-center justify-between px-4 h-16 relative z-[51]">
              <div className="w-10 flex items-center">
                {!isHomePage && (
                  <button
                    onClick={handleBackClick}
                    className="text-gray-500 dark:text-grey-400 hover:text-brand-primary transition-colors duration-200 flex items-center"
                    aria-label="Go back"
                  >
                    <ChevronLeftIcon className="w-6 h-6" />
                  </button>
                )}
              </div>
              <div
                className="flex items-baseline space-x-2 cursor-pointer"
                onClick={handleTitleClick}
              >
                <span className="font-garamond text-xl text-brand-primary">Luminous</span>
                <span className="text-sm text-gray-500 dark:text-grey-300 font-garamond">by Rupert Spira</span>
              </div>
              <div className="w-10" /> {/* Empty spacer to balance the layout */}
            </div>
          </header>

          {/* Main content */}
          <main className="relative px-3 md:px-0" style={{ minHeight: 'calc(100vh - 80px)' }}>
            {children}
          </main>
        </div>
        {/* Bottom safe area filler for devices with home indicator or rounded corners */}
        <div
          className="fixed bottom-0 left-0 right-0 z-[60] bg-white dark:bg-grey-900"
          style={{
            height: 'env(safe-area-inset-bottom, 0px)',
            maxWidth: '600px',
            margin: '0 auto',
            pointerEvents: 'none', // Don't block touch events for scrolling
            backgroundColor: typeof window !== 'undefined' && document.documentElement.classList.contains('dark') ? '#111827' : undefined
          }}
        />
      </div>
    </div>
  );
};

export default MobileLayout;
